import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { NavBar } from '../../components/NavBar';
import { ApplianceScanner } from '../../components/ApplianceScanner';
import { ManualFinder } from '../../components/ManualFinder';
import { api } from '../../lib/api';
import type { Home, Appliance } from '@home-guard/shared';

type ApplianceRow = Appliance & {
  btu_rating?: number | null;
  zone?: string | null;
};

const CURRENT_YEAR = new Date().getFullYear();

const APPLIANCE_TYPES = [
  { value: 'hvac',          label: 'HVAC',          icon: '❄️', lifespan: 15 },
  { value: 'water_heater',  label: 'Water Heater',  icon: '🔥', lifespan: 10 },
  { value: 'washer',        label: 'Washer',        icon: '🫧', lifespan: 11 },
  { value: 'dryer',         label: 'Dryer',         icon: '💨', lifespan: 13 },
  { value: 'refrigerator',  label: 'Refrigerator',  icon: '🧊', lifespan: 12 },
  { value: 'fireplace',     label: 'Fireplace',     icon: '🔥', lifespan: 40 },
  { value: 'ac_window',     label: 'Window AC',     icon: '🌬', lifespan: 10 },
  { value: 'ac_mini_split', label: 'Mini Split',    icon: '❄️', lifespan: 20 },
  { value: 'dishwasher',    label: 'Dishwasher',    icon: '🍽', lifespan:  9 },
  { value: 'generator',      label: 'Generator',      icon: '⚡', lifespan: 20 },
  { value: 'gas_range',     label: 'Gas Range',      icon: '🔥', lifespan: 15 },
  { value: 'electric_range', label: 'Electric Range', icon: '🍳', lifespan: 15 },
  { value: 'glass_top_range', label: 'Glass Cooktop', icon: '🍳', lifespan: 15 },
  { value: 'oven',          label: 'Wall Oven',       icon: '🍳', lifespan: 15 },
  { value: 'microwave',     label: 'Microwave',       icon: '📟', lifespan: 10 },
  { value: 'range_hood',    label: 'Range Hood',      icon: '🌬', lifespan: 20 },
  { value: 'other',         label: 'Other',           icon: '🔧', lifespan: 15 },
] as const;

type ApplianceType = typeof APPLIANCE_TYPES[number]['value'];
const AC_TYPES: ApplianceType[] = ['hvac', 'ac_window', 'ac_mini_split'];

const addSchema = z.object({
  type: z.string().min(1),
  make:  z.string().max(100).trim().optional(),
  model: z.string().max(100).trim().optional(),
  install_year: z.preprocess(v => (v === '' || v == null ? null : Number(v)),
    z.number().int().min(1900).max(CURRENT_YEAR + 1).nullable()),
  location: z.string().max(200).trim().optional(),
  unit_type: z.enum(['central','window','mini_split','portable','']).optional(),
  btu_rating: z.preprocess(v => (v === '' || v == null ? null : Number(v)),
    z.number().int().min(1000).max(500000).nullable()),
  water_heater_type: z.enum(['tank_gas','tank_electric','tankless_gas','tankless_electric','heat_pump','solar_thermal','']).optional(),
  notes: z.string().max(1000).trim().optional(),
});

type AddForm = z.infer<typeof addSchema>;

function typeInfo(type: string) {
  return APPLIANCE_TYPES.find(t => t.value === type) ?? { label: type, icon: '🔧', lifespan: 15 };
}

function ageColor(age: number, lifespan: number) {
  if (age >= lifespan) return 'text-red-600 bg-red-50';
  if (age >= lifespan - 2) return 'text-amber-600 bg-amber-50';
  return 'text-green-700 bg-green-50';
}

interface PendingManual {
  applianceId: string;
  brand: string | null;
  model: string | null;
}

export function ApplianceRegistry() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [pendingManual, setPendingManual] = useState<PendingManual | null>(null);

  const { data: homesData } = useQuery({
    queryKey: ['homes'],
    queryFn: () => api.get<{ data: Home[] }>('/homes'),
  });
  const homes = homesData?.data ?? [];
  const home = homes.find(h => h.is_primary) ?? homes[0];

  const { data: appliancesData, isLoading } = useQuery({
    queryKey: ['appliances', home?.id ?? ''],
    queryFn: () => api.get<{ data: ApplianceRow[] }>(`/homes/${home!.id}/appliances`),
    enabled: !!home,
  });
  const appliances = appliancesData?.data ?? [];

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete<{ success: boolean }>(`/appliances/${id}`),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['appliances'] }); },
  });

  const editing = editingId ? appliances.find(a => a.id === editingId) ?? null : null;

  return (
    <div className="min-h-dvh bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-700 to-blue-900 pt-12 pb-5 px-4">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/app/profile')} className="flex items-center gap-1 text-white/80 text-sm" style={{ minHeight: 44 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Profile
          </button>
          <div className="text-center">
            <h1 className="text-white font-bold text-lg">Appliances</h1>
            <p className="text-white/60 text-xs">{appliances.length} registered</p>
          </div>
          <div style={{ width: 70 }} />
        </div>
      </div>

      <div className="px-4 pt-4 space-y-3">
        {isLoading && (
          <div className="flex justify-center py-8">
            <div className="w-7 h-7 border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!isLoading && appliances.length === 0 && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
            <p className="text-4xl mb-3">🔧</p>
            <p className="font-semibold text-gray-800 mb-1">No appliances yet</p>
            <p className="text-sm text-gray-500">Add your HVAC, water heater, and other appliances to generate maintenance tasks.</p>
          </div>
        )}

        {appliances.map(appliance => {
          const info = typeInfo(appliance.type);
          const age = appliance.install_year ? CURRENT_YEAR - appliance.install_year : null;
          const isAC = AC_TYPES.includes(appliance.type as ApplianceType);

          return (
            <div key={appliance.id} className="bg-white rounded-2xl shadow-sm p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-xl flex-shrink-0">
                  {info.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{info.label}</p>
                      {(appliance.make ?? appliance.model) && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {[appliance.make, appliance.model].filter(Boolean).join(' ')}
                        </p>
                      )}
                    </div>
                    {age !== null && (
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${ageColor(age, info.lifespan)}`}>
                        {age}yr old
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                    {appliance.install_year && (
                      <span className="text-xs text-gray-400">Installed {appliance.install_year}</span>
                    )}
                    {isAC && appliance.unit_type && (
                      <span className="text-xs text-gray-400 capitalize">{appliance.unit_type.replace(/_/g,' ')}</span>
                    )}
                    {appliance.type === 'water_heater' && appliance.water_heater_type && (
                      <span className="text-xs text-gray-400 capitalize">{appliance.water_heater_type.replace(/_/g,' ')}</span>
                    )}
                    {appliance.location && (
                      <span className="text-xs text-gray-400">{appliance.location}</span>
                    )}
                    {isAC && appliance.btu_rating && (
                      <span className="text-xs text-gray-400">{appliance.btu_rating.toLocaleString()} BTU</span>
                    )}
                  </div>

                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => { setEditingId(appliance.id); setShowModal(true); }}
                      className="text-xs text-blue-600 font-medium px-3 py-1.5 bg-blue-50 rounded-lg"
                      style={{ minHeight: 32 }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Remove ${info.label}?`)) {
                          deleteMutation.mutate(appliance.id);
                        }
                      }}
                      className="text-xs text-red-500 font-medium px-3 py-1.5 bg-red-50 rounded-lg"
                      style={{ minHeight: 32 }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* FAB */}
      {home && (
        <button
          onClick={() => { setEditingId(null); setShowModal(true); }}
          className="fixed bottom-24 right-4 w-14 h-14 bg-blue-600 text-white rounded-full text-3xl shadow-lg flex items-center justify-center z-40"
          aria-label="Add appliance"
        >
          +
        </button>
      )}

      {showModal && home && (
        <ApplianceModal
          homeId={home.id}
          existing={editing}
          onClose={() => { setShowModal(false); setEditingId(null); }}
          onSuccess={(saved) => {
            setShowModal(false);
            setEditingId(null);
            void queryClient.invalidateQueries({ queryKey: ['appliances'] });
            void queryClient.invalidateQueries({ queryKey: ['tasks'] });
            // Show manual finder if the appliance has a brand or model
            if (saved.applianceId && (saved.brand || saved.model)) {
              setPendingManual(saved);
            }
          }}
        />
      )}

      {pendingManual && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            zIndex: 200, display: 'flex', alignItems: 'flex-end',
          }}
          onClick={() => setPendingManual(null)}
        >
          <div
            style={{
              width: '100%', background: '#fff', borderRadius: '20px 20px 0 0',
              padding: '24px 20px 48px', maxHeight: '92dvh', overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111827' }}>📖 Find your manual</h2>
              <button
                onClick={() => setPendingManual(null)}
                style={{ background: 'none', border: 'none', fontSize: 22, color: '#9ca3af', cursor: 'pointer', padding: 4 }}
              >
                ✕
              </button>
            </div>
            {(pendingManual.brand || pendingManual.model) && (
              <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>
                {[pendingManual.brand, pendingManual.model].filter(Boolean).join(' ')}
              </p>
            )}
            <ManualFinder
              applianceId={pendingManual.applianceId}
              brand={pendingManual.brand}
              model={pendingManual.model}
              onDone={() => {
                setPendingManual(null);
                void queryClient.invalidateQueries({ queryKey: ['tasks'] });
              }}
              onSkip={() => setPendingManual(null)}
            />
          </div>
        </div>
      )}

      <NavBar />
    </div>
  );
}

function ApplianceModal({
  homeId, existing, onClose, onSuccess,
}: {
  homeId: string;
  existing: ApplianceRow | null;
  onClose: () => void;
  onSuccess: (saved: PendingManual) => void;
}) {
  const [showScanner, setShowScanner] = useState(false);

  const {
    register, handleSubmit, watch, setValue,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<AddForm>({
    resolver: zodResolver(addSchema),
    defaultValues: {
      type: existing?.type ?? '',
      make:  existing?.make ?? '',
      model: existing?.model ?? '',
      install_year: existing?.install_year ?? null,
      location: existing?.location ?? '',
      unit_type: (existing?.unit_type as AddForm['unit_type']) ?? '',
      btu_rating: existing?.btu_rating ?? null,
      water_heater_type: (existing?.water_heater_type as AddForm['water_heater_type']) ?? '',
      notes: existing?.notes ?? '',
    },
  });

  const selectedType = watch('type');
  const selectedWHT = watch('water_heater_type');
  const isAC = AC_TYPES.includes(selectedType as ApplianceType);
  const isWaterHeater = selectedType === 'water_heater';

  const onSubmit = async (data: AddForm) => {
    try {
      const payload = {
        type: data.type,
        ...(data.make        ? { make:         data.make }        : {}),
        ...(data.model       ? { model:        data.model }       : {}),
        ...(data.install_year !== null ? { install_year: data.install_year } : {}),
        ...(data.location    ? { location:     data.location }    : {}),
        ...(data.unit_type   ? { unit_type:    data.unit_type }   : {}),
        ...(data.btu_rating !== null  ? { btu_rating:  data.btu_rating }  : {}),
        ...(data.water_heater_type ? { water_heater_type: data.water_heater_type } : {}),
        ...(data.notes       ? { notes:        data.notes }       : {}),
      };

      if (existing) {
        await api.put(`/appliances/${existing.id}`, payload);
        onSuccess({ applianceId: existing.id, brand: data.make ?? null, model: data.model ?? null });
      } else {
        const created = await api.post<ApplianceRow>(`/homes/${homeId}/appliances`, payload);
        onSuccess({ applianceId: created.id, brand: data.make ?? null, model: data.model ?? null });
      }
    } catch (err) {
      setError('root', { message: err instanceof Error ? err.message : 'Failed to save.' });
    }
  };

  const input = 'w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-white outline-none focus:border-blue-500';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end" onClick={onClose}>
      <div className="w-full bg-white rounded-t-3xl p-5 max-h-[90dvh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-gray-900">{existing ? 'Edit' : 'Add'} Appliance</h2>
          <button onClick={onClose} className="text-gray-400 text-2xl p-1" style={{ minHeight: 44 }}>✕</button>
        </div>

        {showScanner && (
          <ApplianceScanner
            homeId={homeId}
            applianceType={watch('type') || ''}
            onComplete={(data) => {
              if (data.type) setValue('type', data.type);
              if (data.make) setValue('make', data.make);
              if (data.model) setValue('model', data.model);
              if (data.install_year !== null) setValue('install_year', data.install_year);
              if (data.btu_rating !== null) setValue('btu_rating', data.btu_rating);
              if (data.notes) setValue('notes', data.notes);
              setShowScanner(false);
            }}
            onClose={() => setShowScanner(false)}
          />
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Scanner shortcut — only when adding a new appliance */}
          {!existing && (
            <button
              type="button"
              onClick={() => setShowScanner(true)}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-purple-300 text-purple-700 font-semibold text-sm bg-purple-50 hover:bg-purple-100 transition-colors"
              style={{ minHeight: 48 }}
            >
              <span className="text-lg">📷</span>
              Scan appliance label instead
            </button>
          )}

          {/* Type selector grid */}
          {!existing && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Type</p>
              <div className="grid grid-cols-4 gap-2">
                {APPLIANCE_TYPES.map(t => (
                  <button
                    key={t.value} type="button"
                    onClick={() => setValue('type', t.value)}
                    className={`flex flex-col items-center gap-1 p-2 rounded-xl border text-xs font-medium transition-all ${
                      selectedType === t.value
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'border-gray-200 text-gray-600 bg-white'
                    }`}
                    style={{ minHeight: 60 }}
                  >
                    <span className="text-lg">{t.icon}</span>
                    <span className="leading-tight text-center">{t.label}</span>
                  </button>
                ))}
              </div>
              {errors.type && <p className="text-red-500 text-xs mt-1">Select a type</p>}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Make</label>
              <input {...register('make')} placeholder="e.g. Carrier" className={input} />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Model</label>
              <input {...register('model')} placeholder="e.g. Infinity 21" className={input} />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Install Year</label>
              <input {...register('install_year')} type="number" placeholder="e.g. 2018" className={input} />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Location</label>
              <input {...register('location')} placeholder="e.g. Garage" className={input} />
            </div>
          </div>

          {isAC && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Unit Type</label>
                <select {...register('unit_type')} className={input}>
                  <option value="">Select…</option>
                  <option value="central">Central</option>
                  <option value="window">Window</option>
                  <option value="mini_split">Mini Split</option>
                  <option value="portable">Portable</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">BTU Rating</label>
                <input {...register('btu_rating')} type="number" placeholder="e.g. 36000" className={input} />
              </div>
            </div>
          )}

          {isWaterHeater && (
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Water Heater Type</label>
              <select {...register('water_heater_type')} className={input}>
                <option value="">Select type…</option>
                <option value="tank_gas">Tank — Gas</option>
                <option value="tank_electric">Tank — Electric</option>
                <option value="tankless_gas">Tankless — Gas</option>
                <option value="tankless_electric">Tankless — Electric</option>
                <option value="heat_pump">Heat Pump</option>
                <option value="solar_thermal">Solar Thermal</option>
              </select>
              {selectedWHT && (
                <div className="mt-2 rounded-xl bg-blue-50 p-3 text-xs text-blue-900 space-y-1 leading-relaxed">
                  {selectedWHT === 'tank_gas' && (
                    <><strong>Tank Gas</strong> — Most common. Low upfront cost (~$600–1,200). Lifespan 10–12 yrs. Needs annual flush, anode rod check every 3 yrs, T&amp;P valve test yearly.</>
                  )}
                  {selectedWHT === 'tank_electric' && (
                    <><strong>Tank Electric</strong> — No gas required. Slower recovery than gas. Lifespan 10–15 yrs. Higher operating cost than heat pump. Same maintenance as gas tank minus combustion checks.</>
                  )}
                  {selectedWHT === 'tankless_gas' && (
                    <><strong>Tankless Gas</strong> — Unlimited hot water, 20+ yr lifespan. Requires annual descaling (critical in hard water areas) and venting inspection. Higher upfront cost (~$1,000–3,500).</>
                  )}
                  {selectedWHT === 'tankless_electric' && (
                    <><strong>Tankless Electric</strong> — Compact, no venting required. Lower flow rate than gas. Annual descaling and inlet filter cleaning. Good for point-of-use applications.</>
                  )}
                  {selectedWHT === 'heat_pump' && (
                    <><strong>Heat Pump</strong> — Most efficient electric option — saves $300–500/year vs. standard electric. Needs air filter cleaned every 3 months. Requires 1,000+ cu ft of space. 12–15 yr lifespan.</>
                  )}
                  {selectedWHT === 'solar_thermal' && (
                    <><strong>Solar Thermal</strong> — Lowest operating cost in sunny climates. Has a backup electric or gas element. Needs annual collector inspection and glycol fluid check every 3–5 years.</>
                  )}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="text-xs text-gray-500 mb-1 block">Notes</label>
            <textarea {...register('notes')} rows={2} placeholder="Any additional details…" className={input + ' resize-none'} />
          </div>

          {errors.root && (
            <div className="bg-red-50 rounded-xl p-3 text-red-600 text-sm">{errors.root.message}</div>
          )}

          <button
            type="submit" disabled={isSubmitting}
            className="w-full py-3.5 bg-blue-600 text-white rounded-xl font-semibold text-base disabled:opacity-60"
          >
            {isSubmitting ? 'Saving…' : (existing ? 'Save changes' : 'Add appliance')}
          </button>
        </form>
      </div>
    </div>
  );
}
